/**
 * BillingAddressStep.tsx
 *
 * Conditional checkout step inserted between Plan and Payment when the
 * cart contains a domain REGISTRATION (transfers/existing skip this).
 * AWS Route 53 Domains requires full WHOIS contact info — first/last name,
 * street, city, state, postal code, country, phone, email. Without these
 * the registration silently fails server-side with `awaiting_admin` and
 * the customer ends up with a paid order and no domain.
 *
 * The user's name + email + phone are pre-populated from their profile.
 * Address is the field they actually have to type (since signup doesn't
 * collect it).
 */
import React, { useState } from 'react';
import type { CheckoutData } from '../../pages/checkout/HostingCheckout';

interface Props {
  data: CheckoutData;
  onNext: (data: Partial<CheckoutData>) => void;
  onBack: () => void;
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'IN', name: 'India' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'JP', name: 'Japan' },
];

const BillingAddressStep: React.FC<Props> = ({ data, onNext, onBack }) => {
  const [street, setStreet] = useState(data.contactInfo.address.street || '');
  const [city, setCity] = useState(data.contactInfo.address.city || '');
  const [state, setState] = useState(data.contactInfo.address.state || '');
  const [zipCode, setZipCode] = useState(data.contactInfo.address.zipCode || '');
  const [country, setCountry] = useState(data.contactInfo.address.country || 'US');
  const [firstName, setFirstName] = useState(data.contactInfo.firstName || '');
  const [lastName, setLastName] = useState(data.contactInfo.lastName || '');
  const [phone, setPhone] = useState(data.contactInfo.phone || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!firstName.trim()) e.firstName = 'First name is required';
    if (!lastName.trim()) e.lastName = 'Last name is required';
    if (!phone.trim()) e.phone = 'Phone number is required';
    if (!street.trim()) e.street = 'Street address is required';
    if (!city.trim()) e.city = 'City is required';
    if (!state.trim()) e.state = 'State / province is required';
    if (!zipCode.trim()) e.zipCode = 'Postal / ZIP code is required';
    if (!country.trim()) e.country = 'Country is required';
    if (country === 'US' && !US_STATES.includes(state.toUpperCase().trim())) {
      e.state = 'For US, use 2-letter state code (e.g. CA, TX, NY)';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      // Persist the address on the user's customer profile via API so it's
      // available for future orders + matches the order contact_info.
      try {
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_API_URL || '';
        await fetch(`${apiBase}/api/auth/profile`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phone: phone.trim(),
            billingAddress: {
              street: street.trim(),
              city: city.trim(),
              state: state.trim().toUpperCase(),
              zipCode: zipCode.trim(),
              country: country.trim(),
            },
          }),
        });
      } catch (e) {
        // Profile save is best-effort — the order's contact_info is what
        // actually matters for the domain registration. We don't want a
        // profile-save 500 to block the order.
        console.warn('Profile save failed; continuing with order anyway:', e);
      }

      onNext({
        contactInfo: {
          ...data.contactInfo,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim(),
          address: {
            street: street.trim(),
            city: city.trim(),
            state: state.trim().toUpperCase(),
            zipCode: zipCode.trim(),
            country: country.trim(),
          },
        },
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Billing &amp; WHOIS Contact</h2>
        <p className="text-sm text-gray-600">
          You&apos;re registering a new domain. ICANN + your domain registrar (AWS Route 53) require
          complete contact info for every domain registration. We&apos;ll save these details to your
          account for future orders too.
        </p>
      </div>

      {Object.keys(errors).length > 0 && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
          Please fill in the highlighted fields.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First name" required value={firstName} onChange={setFirstName} error={errors.firstName} />
        <Field label="Last name" required value={lastName} onChange={setLastName} error={errors.lastName} />
        <Field
          label="Phone (with country code, e.g. +1 415 555 1234)"
          required
          value={phone}
          onChange={setPhone}
          error={errors.phone}
          className="md:col-span-2"
        />
        <Field
          label="Street address"
          required
          value={street}
          onChange={setStreet}
          error={errors.street}
          className="md:col-span-2"
          placeholder="123 Market St, Apt 4B"
        />
        <Field label="City" required value={city} onChange={setCity} error={errors.city} />
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            State / Province <span className="text-red-500">*</span>
          </label>
          {country === 'US' ? (
            <select
              value={state}
              onChange={e => setState(e.target.value)}
              className={`w-full border rounded px-3 py-2 text-sm ${errors.state ? 'border-red-400' : 'border-gray-300'}`}
              disabled={busy}
            >
              <option value="">Select state</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <input
              type="text"
              value={state}
              onChange={e => setState(e.target.value)}
              placeholder="State / region"
              className={`w-full border rounded px-3 py-2 text-sm ${errors.state ? 'border-red-400' : 'border-gray-300'}`}
              disabled={busy}
            />
          )}
          {errors.state && <p className="text-xs text-red-600 mt-1">{errors.state}</p>}
        </div>
        <Field
          label={country === 'US' ? 'ZIP code' : 'Postal code'}
          required
          value={zipCode}
          onChange={setZipCode}
          error={errors.zipCode}
        />
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            value={country}
            onChange={e => setCountry(e.target.value)}
            className={`w-full border rounded px-3 py-2 text-sm ${errors.country ? 'border-red-400' : 'border-gray-300'}`}
            disabled={busy}
          >
            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          {errors.country && <p className="text-xs text-red-600 mt-1">{errors.country}</p>}
        </div>
      </div>

      <div className="mt-6 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
        <strong>Privacy:</strong> Your name, address, phone, and email are sent to the domain
        registrar to register <code>{data.domainName}</code>. You can enable WHOIS privacy in your
        domain settings after the order completes.
      </div>

      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          disabled={busy}
          className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={submit}
          disabled={busy}
          className="px-5 py-2 text-sm bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-medium rounded hover:from-orange-600 hover:to-yellow-600 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Continue to Payment'}
        </button>
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  error?: string;
  placeholder?: string;
  className?: string;
}> = ({ label, value, onChange, required, error, placeholder, className }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full border rounded px-3 py-2 text-sm ${error ? 'border-red-400' : 'border-gray-300'}`}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

export default BillingAddressStep;
